import React, { Component } from "react";
import moment from "moment";
import { injectIntl } from "react-intl";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { Button } from "antd";
import RightsManagerStats from "./RightsManagerStats";
import RightsManagerFilters from "./RightsManagerFilters";
import RightsManagerTree from "./RightsManagerTree";
import RightsManagerList from "./RightsManagerList";
import CollectionMiddleView from "../collection/CollectionMiddleView";
import RightsManagerTour, { shouldShowTour } from "./RightsManagerTour";
import RightsAssignModal from "./RightsAssignModal";
import UndoBanner from "./UndoBanner";
import {
  openRightsViewer,
  closeRightsViewer,
  getRightsSummary,
  getRightsTreeNode,
  getRightsList,
  assignRightsMany,
  revertRightsOperation,
  exportRightsReport,
} from "../../actions/RightsAction";
import { RIGHTS_STATUS_UNASSIGNED } from "../../constants/rights-constants";
import { sendNotification } from "../../utils";
import "./RightsManager.css";

const DEFAULT_LIST = {
  loading: false,
  error: false,
  rows: [],
  total: 0,
  page: 1,
  limit: 50,
  orderBy: "-createdAt"
};

class RightsManager extends Component {
  state = {
    rightsState: undefined,
    // Filtro por tipo. [] significa "todos": el backend trata la lista vacía y
    // los cuatro tipos igual, así que no hace falta distinguirlos acá.
    types: [],
    // Lo que está elegido en el selector pero todavía no se aplicó con "Ir".
    // Ver handleChangeTypes / handleApplyTypes.
    typesDraft: [],
    // El estado también espera a "Ir". Las tarjetas KPI lo aplican al instante
    // y sincronizan el borrador, para que el selector no quede desfasado.
    rightsStateDraft: undefined,
    // Punto 7 de Ricardo: se quitó el selector "Vista", así que esto queda fijo
    // en "tree" y la vista de lista no es alcanzable hoy. Se deja el estado y
    // la rama del render —y RightsManagerList— porque devolverla es volver a
    // poner el selector; borrarlo sería tirar una vista que ya funciona.
    view: "tree",
    expandedKeys: new Set(),
    selected: new Map(),
    showAssignModal: false,
    // Flujo de "Quitar asignación" en curso. Ver handleRemoveAssignment.
    removing: false,
    // Recorrido de bienvenida: sólo la primera visita de este navegador.
    showTour: shouldShowTour()
  };

  componentDidMount() {
    if (this.isRightsActive(this.props)) {
      this.props.getRightsSummary(
        this.props.workclient,
        this.props.user._id,
        this.state.types
      );
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.isRightsActive(prevProps) && this.isRightsActive(this.props)) {
      // Con el filtro por tipo puesto, pedir el resumen sin `types` hace que
      // las tarjetas cuenten sobre TODA la librería y el panel de abajo sobre
      // el recorte: dos números distintos para lo mismo.
      this.props.getRightsSummary(
        this.props.workclient,
        this.props.user._id,
        this.state.types
      );
    }

    // Assign/revert exitosos: la selección y los conteos ya no valen, y las
    // 4 tarjetas de arriba también cambiaron.
    const assignJustFinished =
      prevProps.loadingAssign && !this.props.loadingAssign && !this.props.errorAssign;
    const revertJustFinished =
      prevProps.loadingRevert && !this.props.loadingRevert && !this.props.errorRevert;
    const exportJustFailed =
      prevProps.loadingExport && !this.props.loadingExport && this.props.errorExport;

    const assignJustFailed =
      prevProps.loadingAssign && !this.props.loadingAssign && this.props.errorAssign;

    if (assignJustFinished || revertJustFinished) {
      // El modal se cierra ACÁ, desde el padre, y no sólo desde su propio
      // temporizador. Limpiar la selección cambia el `key` del modal y lo
      // remonta en el mismo commit en que su setTimeout está por cerrarlo:
      // depender de esa carrera es la razón de que a veces quedara abierto.
      this.setState({ selected: new Map(), showAssignModal: false });
      this.refreshTree();
      this.props.getRightsSummary(
        this.props.workclient,
        this.props.user._id,
        this.state.types
      );
    }

    // Desde el visor se puede cambiar el archivo (derechos, nombre, moverlo,
    // borrarlo), así que al cerrarlo se refresca el árbol y las tarjetas. En
    // SILENCIO (pedido de Ricardo): antes cada carpeta abierta pasaba a
    // "cargando" y las tarjetas a "—", y se veía recargar el módulo entero.
    // Ahora todo queda como estaba y sólo cambian los valores al llegar. Los
    // derechos guardados adentro ya se reflejan al instante en la fila
    // (ASSET_RIGHTS_STATE_UPDATED en RightsReducer).
    if (prevProps.viewerAbierto && !this.props.viewerAbierto) {
      this.refreshTree({ silent: true });
      this.props.getRightsSummary(
        this.props.workclient,
        this.props.user._id,
        this.state.types,
        { silent: true }
      );
    }

    if (this.state.removing && (assignJustFinished || assignJustFailed)) {
      const { intl, lastOperation } = this.props;
      this.setState({ removing: false });

      if (assignJustFailed) {
        // "Ya estaban así" no es un fallo: avisar en rojo haría pensar que se
        // rompió algo cuando sólo no había nada que cambiar.
        const code = this.props.errorAssignCode;
        const porCodigo = {
          NothingUpdated: "rights_manager_assign_nothing_updated",
          NoChanges: "rights_manager_assign_no_changes",
          EmptySelection: "rights_manager_assign_empty_selection"
        };
        sendNotification(
          code === "NoChanges" ? "info" : "error",
          intl,
          intl.formatMessage({
            id: porCodigo[code] || "rights_manager_assign_error"
          })
        );
      } else {
        sendNotification(
          "success",
          intl,
          intl.formatMessage(
            { id: "rights_manager_remove_success" },
            { count: (lastOperation && lastOperation.updated) || 0 }
          )
        );
      }
    }

    if (exportJustFailed) {
      sendNotification(
        "error",
        this.props.intl,
        this.props.intl.formatMessage({ id: "rights_manager_export_error" })
      );
    }
  }

  isRightsActive = props => Boolean(
    props.modules && props.modules.some(module => module.name === "useRights" && module.activate)
  );

  refreshTree = ({ silent = false } = {}) => {
    const { workclient, user, nodes } = this.props;
    const { expandedKeys, rightsState, types } = this.state;
    ["root", ...expandedKeys].forEach(key => {
      if (!nodes[key]) return;
      this.props.getRightsTreeNode(
        workclient,
        user._id,
        key,
        key === "root" ? null : key,
        rightsState,
        types,
        { silent }
      );
    });
  };

  fetchNode = (nodeKey, parentId) => {
    this.props.getRightsTreeNode(
      this.props.workclient,
      this.props.user._id,
      nodeKey,
      parentId,
      this.state.rightsState,
      this.state.types
    );
  };

  /** El selector de la barra sólo mueve el borrador; aplica "Ir". */
  handleChangeStateDraft = rightsStateDraft => {
    this.setState({ rightsStateDraft });
  };

  handleChangeState = rightsState => {
    this.setState(
      { rightsState, rightsStateDraft: rightsState },
      this.refreshActiveView
    );
  };

  /**
   * El filtro por tipo recorta el universo entero, no sólo el panel: los KPI
   * tienen que recontar o las tarjetas dirían un total y la tabla otro.
   */
  /** Sólo mueve el borrador: no consulta hasta que se aprieta "Ir". */
  handleChangeTypes = typesDraft => {
    this.setState({ typesDraft });
  };

  /**
   * Punto 12 de Ricardo. El filtro por tipo recorta el universo entero, no sólo
   * el panel: los KPI tienen que recontar o las tarjetas dirían un total y la
   * tabla otro.
   */
  handleApplyTypes = () => {
    this.setState({
      types: this.state.typesDraft,
      rightsState: this.state.rightsStateDraft,
      selected: new Map()
    }, () => {
      this.props.getRightsSummary(
        this.props.workclient,
        this.props.user._id,
        this.state.types
      );
      this.refreshActiveView();
    });
  };

  /**
   * Punto 11 de Ricardo. Suelta los dos filtros de una: antes había que abrir
   * cada selector y limpiarlo por separado, y el de tipo es múltiple.
   */
  /**
   * Identidad de la selección actual. Se usa como `key` del modal de asignación
   * para que cada selección distinta estrene instancia: el alcance, el contrato
   * adjunto y el estado elegido no pueden sobrevivir de una selección a otra.
   */
  selectionKey = () =>
    Array.from(this.state.selected.keys())
      .sort()
      .join("|") || "vacia";

  handleClearFilters = () => {
    const { rightsState, rightsStateDraft, types, typesDraft } = this.state;
    if (
      !rightsState &&
      !rightsStateDraft &&
      types.length === 0 &&
      typesDraft.length === 0
    ) {
      return;
    }
    this.setState({
      rightsState: undefined,
      rightsStateDraft: undefined,
      types: [],
      typesDraft: [],
      selected: new Map()
    }, () => {
      this.props.getRightsSummary(
        this.props.workclient,
        this.props.user._id,
        this.state.types
      );
      this.refreshActiveView();
    });
  };

  /** Recarga lo que esté visible: el árbol se recarga solo, la lista no. */
  refreshActiveView = () => {
    if (this.state.view === "list") {
      this.fetchList({ page: 1 });
    }
  };

  fetchList = ({ page, orderBy } = {}) => {
    const { workclient, user, list } = this.props;
    this.props.getRightsList(workclient, user._id, {
      page: page || list.page,
      limit: list.limit,
      orderBy: orderBy || list.orderBy,
      rightsState: this.state.rightsState,
      types: this.state.types
    });
  };

  handleChangeOrder = orderBy => this.fetchList({ page: 1, orderBy });

  handleChangePage = page => this.fetchList({ page });

  /**
   * Clic en un archivo del árbol. Abre el visor de assets del repositorio —el
   * mismo panel de siempre, con la previsualización, las versiones, los tags y
   * el bloque de derechos— en vez de un detalle propio del módulo. El árbol no
   * trae las URLs de media, así que la pieza completa se pide al abrir.
   */
  handleOpenDetail = (asset, nodeKey) => {
    this.props.openRightsViewer(this.props.workclient, asset, nodeKey);
  };

  handleCloseDetail = () => this.props.closeRightsViewer();

  handleCollapseAll = () => {
    this.setState({ expandedKeys: new Set() });
  };

  handleToggleExpand = folderId => {
    this.setState(prev => {
      const next = new Set(prev.expandedKeys);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return { expandedKeys: next };
    });
  };

  handleToggleSelect = (kind, data) => {
    this.setState(prev => {
      const key = `${kind}:${data._id}`;
      const next = new Map(prev.selected);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, { kind, data });
      }
      return { selected: next };
    });
  };

  handleExport = () => {
    const { workclient, user } = this.props;
    const { rightsState } = this.state;
    if (!rightsState) {
      sendNotification(
        "warning",
        this.props.intl,
        this.props.intl.formatMessage({ id: "rights_manager_export_all_notice" })
      );
    }
    this.props.exportRightsReport(workclient, user._id, {
      rightsState: rightsState || undefined,
      // El CSV tiene que traer lo mismo que se está viendo: exportar el parque
      // completo con un filtro activo en pantalla sería una sorpresa fea.
      types: this.state.types.length ? this.state.types.join(",") : undefined
    });
  };

  handleRemoveAssignment = () => {
    const { selected } = this.state;
    if (selected.size === 0) return;
    const objectIds = Array.from(selected.values()).map(({ data }) => data._id);
    // "Quitar" no abre el modal, así que no tiene dónde mostrar el Alert de
    // error: sin esta marca el usuario no recibía ningún aviso, ni bueno ni
    // malo, y la operación parecía no haber ocurrido.
    this.setState({ removing: true });
    this.props.assignRightsMany(
      this.props.workclient,
      this.props.user._id,
      objectIds,
      { rightsStatus: RIGHTS_STATUS_UNASSIGNED, createdDate: null, dueDate: null },
      this.props.intl
    );
  };

  handleAssignDone = () => {
    const { intl } = this.props;
    this.setState({ showAssignModal: false });
    sendNotification(
      "success",
      intl,
      intl.formatMessage({ id: "rights_manager_assign_success" })
    );
  };

  handleRevert = operationId => {
    this.props.revertRightsOperation(
      this.props.workclient,
      this.props.user._id,
      operationId
    );
  };

  render() {
    const { intl, nodes, lastOperation, loadingRevert, loadingAssign, loadingSummary, client, summary, viewerAbierto } = this.props;
    const {
      rightsState,
      types,
      view,
      expandedKeys,
      selected,
      showAssignModal,
      showTour
    } = this.state;
    // Cinturón y tirantes: el store ya completa las claves nuevas al rehidratar
    // (ver completarConDefaults en store/index.js), pero un estado guardado por
    // una versión vieja no debería poder dejar la pantalla en blanco.
    const list = this.props.list || DEFAULT_LIST;

    if (!this.isRightsActive(this.props)) {
      return (
        <div className="rm-page">
          <div className="rm-disabled-state">
            {intl.formatMessage({ id: "rights_manager_disabled" })}
          </div>
        </div>
      );
    }
    const selectionCount = selected.size;
    const selectedValues = Array.from(selected.values());
    const selectedFolders = selectedValues.filter(item => item.kind === "folder").length;
    // Carpetas que abarca la selección: las marcadas más las que cuelgan de
    // ellas. `totalFolders` lo calcula el backend en la misma pasada que cuenta
    // las piezas; el front sólo conoce las ramas que el usuario expandió, así
    // que sin ese dato el resumen decía "1 carpeta" aunque se hubieran
    // seleccionado cientos.
    const reachedFolders = selectedValues.reduce(
      (total, item) =>
        item.kind === "folder"
          ? total + 1 + (Number(item.data.totalFolders) || 0)
          : total,
      0
    );
    const selectedFiles = selectedValues.filter(item => item.kind === "file").length;
    const selectedPieces = selectedValues.reduce((total, item) => {
      return total + (item.kind === "folder" ? item.data.totalPieces || 0 : item.data.isContent === false ? 0 : 1);
    }, 0);
    const clientName = client && client.data && client.data.name;
    const outOfModule = summary.outOfModule || 0;
    const contentTotal = summary.total || 0;
    const updatedAt = summary.updatedAt ? moment(summary.updatedAt).fromNow() : null;
    const canApplySelection = selectedPieces > 0;

    return (
      <div className="rm-page">
        <div className="rm-crumb">
          <span className="rm-crumb-link">{clientName || "Bunkey"}</span>
          <span>/</span>
          <b>{intl.formatMessage({ id: "rights_manager_title" })}</b>
        </div>
        <h1 className="rm-title">
          {intl.formatMessage({ id: "rights_manager_title" })}
        </h1>
        <p className="rm-subtitle">
          {loadingSummary
            ? intl.formatMessage({ id: "rights_manager_summary_loading" })
            : contentTotal > 0 && updatedAt
            ? intl.formatMessage(
                { id: "rights_manager_summary_line" },
                {
                  // El plural elige con el número y muestra el texto ya
                  // formateado. Antes recibía "27.838" como texto: el `#` lo
                  // releía como decimal y mostraba "27,838" (y "5.320"
                  // habría salido "5,32").
                  total: contentTotal,
                  totalLabel: contentTotal.toLocaleString("es-CL"),
                  outOfModule,
                  outOfModuleLabel: outOfModule.toLocaleString("es-CL"),
                  updatedAt
                }
              )
            : intl.formatMessage({ id: "rights_manager_subtitle" })}
        </p>

        <RightsManagerStats
          rightsState={rightsState}
          onSelectState={this.handleChangeState}
        />

        <UndoBanner
          operation={lastOperation}
          onRevert={this.handleRevert}
          reverting={loadingRevert}
        />

        {/* Filtros y barra de selección quedan fijos al scrollear: en una lista
            larga había que volver hasta arriba para asignar o quitar derechos
            de lo que se acababa de marcar. */}
        <div className="rm-sticky">
        <RightsManagerFilters
          rightsState={rightsState}
          onChangeState={this.handleChangeStateDraft}
          types={types}
          onChangeTypes={this.handleChangeTypes}
          typesDraft={this.state.typesDraft}
          rightsStateDraft={this.state.rightsStateDraft}
          onApplyTypes={this.handleApplyTypes}
          onClearFilters={this.handleClearFilters}
          onExport={this.handleExport}
          exporting={this.props.loadingExport}
        />

        {selectionCount > 0 && (
          <div className="rm-selection-bar">
            <span className="rm-selection-check">−</span>
            <b>{intl.formatMessage({ id: "rights_manager_selected_count" }, { count: selectionCount })}</b>
            <span className="rm-selection-detail">
              {/* Sólo se nombra lo que hay: decir "0 archivos" al lado de una
                  carpeta daba a entender que su contenido no se estaba
                  contando, cuando el total sí lo incluye. */}
              {selectedFolders > 0 &&
                ` · ${intl.formatMessage(
                  { id: "rights_manager_selected_scope_folders" },
                  { count: selectedFolders }
                )}`}
              {reachedFolders > selectedFolders &&
                ` ${intl.formatMessage(
                  { id: "rights_manager_selected_subfolders" },
                  { count: reachedFolders - selectedFolders }
                )}`}
              {selectedFiles > 0 &&
                ` · ${intl.formatMessage({ id: "rights_manager_selected_scope_files" }, { count: selectedFiles })}`}
              {selectedPieces > 0 &&
                ` · ${intl.formatMessage(
                  { id: "rights_manager_selected_pieces" },
                  { count: selectedPieces }
                )}`}
            </span>
            <div className="rm-grow" />
            {/* Pedido de Ricardo: al seleccionar, "Asignar derechos de uso"
                va en el color primario para guiar la vista a la acción
                principal. "Quitar asignación" queda neutro. */}
            <Button className={`rm-selection-action${canApplySelection ? " active" : ""}`} disabled={!canApplySelection} onClick={() => this.setState({ showAssignModal: true })}>
              {intl.formatMessage({ id: "rights_manager_assign_action" })}
            </Button>
            <Button className="rm-selection-action" disabled={!canApplySelection} onClick={this.handleRemoveAssignment} loading={loadingAssign}>
              {intl.formatMessage({ id: "rights_manager_remove_action" })}
            </Button>
          </div>
        )}
        </div>

        {view === "tree" ? (
          <RightsManagerTree
            onOpenDetail={this.handleOpenDetail}
            nodes={nodes}
            rightsState={rightsState}
            types={types}
            expandedKeys={expandedKeys}
            selected={selected}
            fetchNode={this.fetchNode}
            onToggleExpand={this.handleToggleExpand}
            onToggleSelect={this.handleToggleSelect}
            onCollapseAll={this.handleCollapseAll}
          />
        ) : (
          <RightsManagerList
            rows={list.rows}
            total={list.total}
            page={list.page}
            limit={list.limit}
            orderBy={list.orderBy}
            loading={list.loading}
            selected={selected}
            onToggleSelect={this.handleToggleSelect}
            onChangeOrder={this.handleChangeOrder}
            onChangePage={this.handleChangePage}
            onOpenDetail={this.handleOpenDetail}
          />
        )}

        {showTour && (
          <RightsManagerTour onClose={() => this.setState({ showTour: false })} />
        )}

        {/* Visor del asset. Es el MISMO panel del repositorio: previsualización,
            versiones, comentarios, tags y el bloque de derechos. Se monta con
            su propio `keyCollection` y se alimenta de `state.rights.viewer`,
            igual que assetsExplore o assetsFavorites con los suyos. */}
        {viewerAbierto && (
          <div className="rm-viewer">
            <CollectionMiddleView keyCollection="rightsManager" />
          </div>
        )}

        {showAssignModal && (
          <RightsAssignModal
            key={this.selectionKey()}
            visible={showAssignModal}
            selection={Array.from(selected.values())}
            workclient={this.props.workclient}
            userId={this.props.user._id}
            onClose={() => this.setState({ showAssignModal: false })}
            onDone={this.handleAssignDone}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  workclient: state.client.workclient,
  client: state.client,
  modules: state.client.data ? state.client.data.modules : [],
  user: state.authUser.user,
  nodes: state.rights.nodes,
  lastOperation: state.rights.lastOperation,
  loadingAssign: state.rights.loadingAssign,
  loadingSummary: state.rights.loadingSummary,
  errorAssign: state.rights.errorAssign,
  errorAssignCode: state.rights.errorAssignCode,
  // Sólo con la pieza ya cargada. CollectionMiddleView asume que SIEMPRE hay un
  // item seleccionado: su componentDidUpdate lee `prevProps.itemSelected._id`
  // sin verificar que exista. Montarlo mientras la consulta está en curso —con
  // `data` vacío— revienta ahí, y por eso el panel no aparecía aunque la
  // respuesta llegara con 200.
  viewerAbierto: state.rights.viewer.data.length > 0,
  loadingRevert: state.rights.loadingRevert,
  errorRevert: state.rights.errorRevert,
  loadingExport: state.rights.loadingExport,
  errorExport: state.rights.errorExport,
  summary: state.rights.summary,
  list: state.rights.list
});

export default injectIntl(
  withRouter(
    connect(mapStateToProps, {
      openRightsViewer,
      closeRightsViewer,
      getRightsSummary,
      getRightsTreeNode,
      getRightsList,
      assignRightsMany,
      revertRightsOperation,
      exportRightsReport,
    })(RightsManager)
  )
);
